/**
 * useReceipts — TanStack Query data layer for the Receipts (Recibos) module.
 *
 * A receipt is the PAYMENT PROOF that closes the flow: approved quote → signed
 * contract → service order → RECEIPT. It is a FLAT entity (header + amount +
 * description + optional issuer signature) with NO status machine and NO remote
 * signature — unlike contracts, everything is done in-app by the issuer.
 *
 * Offline-first applies to the whole lifecycle (create/update/delete) —
 * delegated to useOfflineSync, which enqueues the op and updates the local
 * cache. An `offline_` id must never reach Postgres — see
 * [[orcamigo-offline-sync-uuid-bug]].
 */
import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { supabase } from '@/integrations/supabase/client';
import { checkNetworkStatus } from '@/hooks/useNetworkStatus';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import { cacheReceipts, getCachedReceipts } from '@/lib/offlineDataCache';
import {
  buildReceiptItemRow,
  receiptAmountFrom,
  type ReceiptItemForm,
} from '@/lib/receiptItems';
import type { PaymentMethod } from '@/types/database';
import { toast } from 'sonner';

const RECEIPTS_KEY = 'receipts';

/** Local 'YYYY-MM-DD' for the default issue date (no timezone drift). */
export function todayISODate(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * A single shape used by ALL four origins (avulso, orçamento aprovado, OS
 * concluída, lançamento financeiro pago) to seed the receipt form. Every field
 * is optional; the form fills what it can and the user completes the rest.
 */
export interface ReceiptPrefill {
  quote_id?: string | null;
  service_order_id?: string | null;
  client_id?: string | null;
  finance_entry_id?: string | null;
  payer_name?: string | null;
  payer_document?: string | null;
  payer_address?: string | null;
  amount?: number | null;
  description?: string | null;
  payment_method?: PaymentMethod | string | null;
}

export interface ReceiptInput {
  quote_id?: string | null;
  service_order_id?: string | null;
  client_id?: string | null;
  finance_entry_id?: string | null;
  payer_name?: string | null;
  payer_document?: string | null;
  payer_address?: string | null;
  amount?: number;
  description?: string | null;
  payment_method?: string | null;
  issue_date?: string | null;
  issue_place?: string | null;
  issuer_signature?: string | null;
  issuer_signer_name?: string | null;
  notes?: string | null;
  show_sequence_id?: boolean;
  cancelled_at?: string | null;
  /**
   * Formas ACEITAS e condições combinadas. Espelham `quotes`; convivem com
   * `payment_method` (singular), que é a forma efetivamente USADA.
   * `null` = não informado (legado); `[]` = o usuário desmarcou tudo (#73).
   */
  payment_methods?: string[] | null;
  payment_terms?: string | null;
  /**
   * Linhas discriminadas (lacuna L1). OPCIONAL: `undefined` significa "não
   * mexa nos itens" — é o que mantém intacto todo recibo do histórico, que
   * não tem linha nenhuma. `[]` significa "apague as linhas".
   *
   * Quando há linhas, `amount` é RECALCULADO a partir delas e o valor
   * digitado é ignorado: dois números para a mesma coisa divergem, e o do
   * papel tem que ser o da soma das linhas impressas (#69).
   */
  items?: ReceiptItemForm[];
}

// ===== Read =====

/**
 * Base query: every receipt for the active company. Cached locally so the list
 * keeps working offline. Filtering is client-side to preserve the snapshot.
 */
export function useReceipts() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [RECEIPTS_KEY, companyId],
    enabled: Boolean(user?.id && companyId),
    networkMode: 'offlineFirst',
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
    // A query nasce DESABILITADA (sessão/empresa resolvem depois da montagem), então
    // `refetchOnMount` já passou quando ela habilita: sem o carimbo, o cache semeado
    // conta como FRESCO e a primeira busca nunca sai (#158).
    initialDataUpdatedAt: 0,
    initialData: () => (user?.id ? getCachedReceipts(user.id) ?? undefined : undefined),
    queryFn: async (): Promise<any[]> => {
      if (!user?.id || !companyId) return [];
      const { data, error } = await supabase
        .from('receipts')
        // Embed dos itens: uma consulta so, e o cache offline passa a
        // carregar as linhas junto do recibo. Buscar em separado deixaria o
        // documento sem itens quando reaberto sem rede (o modo de falha do #66).
        .select('*, receipt_items(*)')
        .eq('company_id', companyId)
        .order('issue_date', { ascending: false })
        .order('created_at', { ascending: false });
      if (error) throw error;
      const rows = (data ?? []) as any[];
      cacheReceipts(user.id, rows);
      return rows;
    },
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [RECEIPTS_KEY, companyId] });
  }, [queryClient, companyId]);

  const syncFromCache = useCallback(() => {
    if (!user?.id) return;
    queryClient.setQueryData([RECEIPTS_KEY, companyId], getCachedReceipts(user.id) ?? []);
  }, [queryClient, user?.id, companyId]);

  return { ...query, receipts: query.data ?? [], invalidate, syncFromCache };
}

/** A single receipt by id, from the cached list (works offline). */
export function useReceipt(id: string | undefined) {
  const raw = useReceipts();
  const receipt = useMemo(
    () => (id ? raw.receipts.find((r: any) => r.id === id) ?? null : null),
    [raw.receipts, id],
  );
  return { ...raw, receipt };
}

// ===== Mutations =====

/** FK columns that may carry a temp `offline_` id from an unsynced source. */
const OFFLINE_FK_KEYS = ['quote_id', 'service_order_id', 'client_id', 'finance_entry_id'] as const;

/**
 * Null out any FK still holding a temp `offline_` id before an ONLINE write —
 * the referenced record isn't in Postgres yet, so inserting it would blow up
 * with `invalid input syntax for type uuid`. The receipt keeps its payer
 * snapshot/amount; only the (not-yet-real) link is dropped. Offline writes keep
 * the id so the sync worker can resolve it via the id map (translateOfflineIds).
 */
function stripOfflineFks(fields: Record<string, any>): void {
  for (const key of OFFLINE_FK_KEYS) {
    if (fields[key] && String(fields[key]).startsWith('offline_')) fields[key] = null;
  }
}

/** Normalize an input into the scalar column set stored on `receipts`. */
function toReceiptFields(input: Partial<ReceiptInput>): Record<string, any> {
  const fields: Record<string, any> = {};
  const set = (key: string, value: any) => {
    if (value !== undefined) fields[key] = value;
  };
  set('quote_id', input.quote_id ?? null);
  set('service_order_id', input.service_order_id ?? null);
  set('client_id', input.client_id ?? null);
  set('finance_entry_id', input.finance_entry_id ?? null);
  set('payer_name', input.payer_name ?? null);
  set('payer_document', input.payer_document ?? null);
  set('payer_address', input.payer_address ?? null);
  set('amount', input.amount !== undefined ? Number(input.amount) : undefined);
  set('description', input.description ?? null);
  set('payment_method', input.payment_method ?? null);
  set('issue_date', input.issue_date ?? undefined);
  set('issue_place', input.issue_place ?? null);
  set('issuer_signature', input.issuer_signature ?? null);
  set('issuer_signer_name', input.issuer_signer_name ?? null);
  set('notes', input.notes ?? null);
  set('show_sequence_id', input.show_sequence_id);
  set('cancelled_at', input.cancelled_at);
  set('payment_methods', input.payment_methods ?? null);
  set('payment_terms', input.payment_terms ?? null);
  return fields;
}


/**
 * Grava as linhas do recibo: apaga as que existem e reinsere.
 *
 * Apagar-e-reinserir é o MESMO padrão do orçamento, e a razão é a mesma: o
 * formulário devolve a lista inteira já ordenada, e casar linha a linha exigiria
 * um id estável que o formulário não tem. A consequência conhecida é que o `id`
 * da linha não sobrevive ao save — por isso nada aponta para `receipt_items.id`.
 *
 * `items === undefined` significa "não mexa" e sai sem tocar em nada: é o que
 * mantém intacto o recibo do histórico, que não tem linha nenhuma.
 */
async function persistReceiptItems(
  receiptId: string,
  items: ReceiptItemForm[] | undefined,
): Promise<void> {
  if (items === undefined) return;

  const { error: delError } = await supabase
    .from('receipt_items')
    .delete()
    .eq('receipt_id', receiptId);
  if (delError) throw delError;

  if (items.length === 0) return;

  const rows = items.map((item, index) => ({
    ...buildReceiptItemRow(item, index),
    receipt_id: receiptId,
  }));
  const { error: insError } = await supabase.from('receipt_items').insert(rows);
  if (insError) throw insError;
}

/**
 * O `amount` que vai para a coluna. Havendo linhas, ele é a SOMA delas; não
 * havendo, é o que o usuário digitou.
 *
 * Sem esta função o recibo teria dois números para a mesma coisa — e o do papel
 * (a soma da coluna impressa) divergiria do gravado na primeira edição de item,
 * sem erro nenhum. É a lição do #69 aplicada ao recibo.
 */
function resolveReceiptAmount(input: Partial<ReceiptInput>): number | undefined {
  const fromItems = receiptAmountFrom(input.items);
  if (fromItems !== null) return fromItems;
  return input.amount !== undefined ? Number(input.amount) : undefined;
}

export function useCreateReceipt() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();
  const { saveReceiptOffline } = useOfflineSync();

  return useMutation({
    // offlineFirst: sem isto o TanStack PAUSA a mutation quando o app perde a
    // rede com a tela aberta, e o ramo offline nunca roda (CLAUDE.md #41).
    networkMode: 'offlineFirst' as const,
    mutationFn: async (input: ReceiptInput) => {
      if (!user?.id) throw new Error('Sem sessão');
      if (!companyId) throw new Error('Nenhuma empresa ativa');

      const fields = toReceiptFields(input);
      // O valor sai das LINHAS quando elas existem (ver `resolveReceiptAmount`).
      const amount = resolveReceiptAmount(input);
      if (amount !== undefined) fields.amount = amount;
      if (fields.issue_date === undefined) fields.issue_date = todayISODate();
      const online = await checkNetworkStatus();

      if (!online) {
        /*
         * Os itens viajam DENTRO da operação (`_items`): o executor os insere
         * depois de criar o recibo, quando a linha já tem id de verdade.
         *
         * E vão TAMBÉM como `receipt_items`, no formato que o servidor devolve,
         * porque é essa a chave que a tela e o PDF leem. Sem ela, o recibo
         * criado sem rede reabriria SEM as linhas até a fila sincronizar — o
         * modo de falha do #66. O executor monta o payload de `receipts` por
         * lista explícita, então a chave extra é ignorada com segurança.
         */
        const offlineItems = (input.items ?? []).map((item, index) =>
          buildReceiptItemRow(item, index),
        );
        const id = saveReceiptOffline({
          receipt: { ...fields, _items: input.items ?? [], receipt_items: offlineItems },
          userId: user.id,
          companyId,
        });
        queryClient.setQueryData([RECEIPTS_KEY, companyId], getCachedReceipts(user.id) ?? []);
        return { offline: true as const, id };
      }

      // Online insert: a source (quote/OS/finance entry) that hasn't synced yet
      // would leave a temp `offline_` FK — null it so the insert doesn't crash.
      stripOfflineFks(fields);

      // Next per-company sequence.
      const { data: maxSeqRow } = await supabase
        .from('receipts')
        .select('sequence_id')
        .eq('company_id', companyId)
        .not('sequence_id', 'is', null)
        .order('sequence_id', { ascending: false })
        .limit(1)
        .maybeSingle();
      const nextSequenceId = ((maxSeqRow as any)?.sequence_id ?? 0) + 1;

      const { data: created, error } = await supabase
        .from('receipts')
        .insert({ ...fields, user_id: user.id, company_id: companyId, sequence_id: nextSequenceId })
        .select('*')
        .single();
      if (error) throw error;

      await persistReceiptItems((created as any).id as string, input.items);

      queryClient.invalidateQueries({ queryKey: [RECEIPTS_KEY, companyId] });
      window.dispatchEvent(new CustomEvent('receipts-updated'));
      return { offline: false as const, id: (created as any).id as string };
    },
    onError: () => toast.error('Não foi possível salvar o recibo.'),
  });
}

export function useUpdateReceipt() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();
  const { updateReceiptOffline } = useOfflineSync();

  return useMutation({
    // offlineFirst: sem isto o TanStack PAUSA a mutation quando o app perde a
    // rede com a tela aberta, e o ramo offline nunca roda (CLAUDE.md #41).
    networkMode: 'offlineFirst' as const,
    mutationFn: async (input: { id: string; updates: Partial<ReceiptInput> }) => {
      if (!user?.id) throw new Error('Sem sessão');
      const online = await checkNetworkStatus();

      const fields = toReceiptFields(input.updates);
      // Never let an update blank a field that wasn't provided.
      Object.keys(fields).forEach((k) => {
        if ((input.updates as any)[k] === undefined) delete fields[k];
      });
      // DEPOIS da limpeza acima, de proposito: o valor derivado das LINHAS
      // nao veio em `updates.amount`, entao a varredura o apagaria.
      const amount = resolveReceiptAmount(input.updates);
      if (amount !== undefined) fields.amount = amount;

      if (!online) {
        // Mesma razão do create: `_items` para o executor, `receipt_items`
        // para a tela (o cache é atualizado espalhando `updates`).
        updateReceiptOffline({
          receiptId: input.id,
          updates:
            input.updates.items === undefined
              ? fields
              : {
                  ...fields,
                  _items: input.updates.items,
                  receipt_items: input.updates.items.map((item, index) =>
                    buildReceiptItemRow(item, index),
                  ),
                },
          userId: user.id,
        });
        queryClient.setQueryData([RECEIPTS_KEY, companyId], getCachedReceipts(user.id) ?? []);
        return;
      }

      // Online update: drop any FK still pointing at an unsynced offline_ record.
      stripOfflineFks(fields);

      let query = supabase.from('receipts').update(fields).eq('id', input.id);
      if (companyId) query = query.eq('company_id', companyId);
      const { error } = await query;
      if (error) throw error;

      await persistReceiptItems(input.id, input.updates.items);

      queryClient.invalidateQueries({ queryKey: [RECEIPTS_KEY, companyId] });
      window.dispatchEvent(new CustomEvent('receipts-updated'));
    },
    onError: () => toast.error('Não foi possível atualizar o recibo.'),
  });
}

export function useDeleteReceipt() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();
  const { deleteReceiptOffline } = useOfflineSync();

  return useMutation({
    // offlineFirst: sem isto o TanStack PAUSA a mutation quando o app perde a
    // rede com a tela aberta, e o ramo offline nunca roda (CLAUDE.md #41).
    networkMode: 'offlineFirst' as const,
    mutationFn: async (id: string) => {
      if (!user?.id) throw new Error('Sem sessão');
      const online = await checkNetworkStatus();

      if (!online) {
        deleteReceiptOffline({ receiptId: id, userId: user.id });
        queryClient.setQueryData([RECEIPTS_KEY, companyId], getCachedReceipts(user.id) ?? []);
        return;
      }

      let query = supabase.from('receipts').delete().eq('id', id);
      if (companyId) query = query.eq('company_id', companyId);
      const { error } = await query;
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: [RECEIPTS_KEY, companyId] });
      window.dispatchEvent(new CustomEvent('receipts-updated'));
    },
    onError: () => toast.error('Não foi possível excluir o recibo.'),
  });
}
